import { HEN_BREEDS } from '../data/breeds';
import type { BreedId } from '../data/types';
import { farmTotals, henStats } from './economy';
import { isFirstOfKind } from './modifiers';
import { makeBox, makeHen, touch } from './state';
import type { GameState, SimCtx } from './types';

/**
 * "What would this hen earn if I placed her in this flock right now?"
 * Evaluated on a clone with the real income model, so shop numbers include
 * first-of-kind, breed specials, rooster aura and everything else.
 * Cached per state revision.
 */
export interface HenPreview {
  layRate: number;
  eggValue: number;
  /** Her own $/s (lay × value). */
  perSec: number;
  /** Change in the farm's steady production, including effects on other hens. */
  gain: number;
  firstOfKind: boolean;
}

const cache = new WeakMap<GameState, { key: string; base: number; results: Map<string, HenPreview> }>();

export function previewHen(s: GameState, ctx: SimCtx, flockId: number, breed: BreedId): HenPreview {
  const key = `${s.rev}|${Math.floor(ctx.wall / 60_000)}|${ctx.localHour}`;
  let entry = cache.get(s);
  if (!entry || entry.key !== key) {
    entry = { key, base: farmTotals(s, ctx, true).production, results: new Map() };
    cache.set(s, entry);
  }
  const rk = `${flockId}|${breed}`;
  const hit = entry.results.get(rk);
  if (hit) return hit;
  const clone = structuredClone(s);
  const flock = clone.flocks.find((f) => f.id === flockId) ?? clone.flocks[0]!;
  let box = flock.boxes.find((b) => !b.hen);
  if (!box) {
    box = makeBox();
    flock.boxes.push(box);
  }
  const hen = makeHen(breed, ctx.wall, 0);
  box.hen = hen;
  touch(clone);
  const st = henStats(clone, ctx, flock, hen, true);
  const result: HenPreview = {
    layRate: st.layRate,
    eggValue: st.eggValue,
    perSec: st.layRate * st.eggValue,
    gain: farmTotals(clone, ctx, true).production - entry.base,
    firstOfKind: isFirstOfKind(flock, hen) && HEN_BREEDS[breed] !== undefined,
  };
  entry.results.set(rk, result);
  return result;
}
